import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  Signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { ROUTE_CONSTS } from '@g99-product-web-ui/shared-util-lib';

import { NotificationEnumType } from '../../../../enums/notification.enum';
import { NotificationFacade } from '../../../../facades/notification.facade';
import {
  IGroupedNotifications,
  INotificationDto,
} from '../../../../interfaces/notification.interface';

const CONTACT_NOTIFICATION_TYPES: string[] = [
  NotificationEnumType.LEAD_GENERATED,
  NotificationEnumType.CONTACT_REPLIES_TO_CONVERSATION,
  NotificationEnumType.INBOX_CONVERSATION_ASSIGNED,
  'CONTACT_REPLIED_TO_CONVERSATION',
  'CONVERSATION_ASSIGNED',
  'INBOX_ASSIGNED',
];

@Component({
  selector: 'g99-lib-notification-list',
  templateUrl: './notification-list.component.html',
  styleUrl: './notification-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class NotificationListComponent {
  @Output() public navigated = new EventEmitter<void>();

  protected readonly groupedNotifications: Signal<IGroupedNotifications>;

  constructor(
    private readonly _facade: NotificationFacade,
    private readonly _router: Router
  ) {
    this.groupedNotifications = this._facade.groupedNotifications;
  }

  protected onNotificationClick(notification: INotificationDto): void {
    this._facade.markOneRead(notification.id);

    if (!this._isContactNotification(notification.type, notification.title)) {
      return;
    }

    const leadId = this._resolveContactId(notification);
    if (leadId) {
      this._router.navigate(['/' + ROUTE_CONSTS.INBOX.base, leadId]);
      this.navigated.emit();
      return;
    }

    const inboxId = this._resolveInboxId(notification);
    if (inboxId) {
      this._router.navigate(['/' + ROUTE_CONSTS.INBOX.base], {
        queryParams: { inboxId },
      });
      this.navigated.emit();
      return;
    }

    if (
      this._isConversationNotification(notification.type, notification.title)
    ) {
      this._router.navigate(['/' + ROUTE_CONSTS.INBOX.base]);
      this.navigated.emit();
    }
  }

  private _isContactNotification(type?: string, title?: string): boolean {
    const normalizedType = (type || '').toUpperCase();
    const normalizedTitle = (title || '').toUpperCase();

    if (
      CONTACT_NOTIFICATION_TYPES.some(
        (contactType) => contactType.toUpperCase() === normalizedType
      )
    ) {
      return true;
    }

    if (
      normalizedType.includes('LEAD') ||
      normalizedType.includes('REPL') ||
      (normalizedType.includes('ASSIGN') &&
        (normalizedType.includes('INBOX') ||
          normalizedType.includes('CONVERSATION')))
    ) {
      return true;
    }

    if (
      normalizedTitle.includes('LEAD') ||
      normalizedTitle.includes('REPL') ||
      (normalizedTitle.includes('ASSIGN') &&
        (normalizedTitle.includes('INBOX') ||
          normalizedTitle.includes('CONVERSATION')))
    ) {
      return true;
    }

    return false;
  }

  private _isConversationNotification(type?: string, title?: string): boolean {
    const normalizedType = (type || '').toUpperCase();
    const normalizedTitle = (title || '').toUpperCase();

    if (
      normalizedType ===
        NotificationEnumType.CONTACT_REPLIES_TO_CONVERSATION.toUpperCase() ||
      normalizedType ===
        NotificationEnumType.INBOX_CONVERSATION_ASSIGNED.toUpperCase() ||
      normalizedType.includes('REPL') ||
      (normalizedType.includes('ASSIGN') &&
        (normalizedType.includes('INBOX') ||
          normalizedType.includes('CONVERSATION')))
    ) {
      return true;
    }

    if (
      normalizedTitle.includes('REPL') ||
      (normalizedTitle.includes('ASSIGN') &&
        (normalizedTitle.includes('INBOX') ||
          normalizedTitle.includes('CONVERSATION')))
    ) {
      return true;
    }

    return false;
  }

  private _getMetadata(raw: Record<string, unknown>): Record<string, unknown> {
    const meta = raw['metadata'];
    if (!meta) {
      return {};
    }
    if (typeof meta === 'object') {
      return meta as Record<string, unknown>;
    }
    if (typeof meta === 'string') {
      try {
        const parsed = JSON.parse(meta);
        if (typeof parsed === 'object' && parsed !== null) {
          return parsed as Record<string, unknown>;
        }
      } catch {
        return {};
      }
    }
    return {};
  }

  private _resolveContactId(notification: INotificationDto): number | null {
    const raw = notification as unknown as Record<string, unknown>;
    const metadata = this._getMetadata(raw);
    const data =
      raw['data'] && typeof raw['data'] === 'object'
        ? (raw['data'] as Record<string, unknown>)
        : {};

    const candidates = [
      raw['contactId'],
      raw['contact_id'],
      raw['leadId'],
      raw['lead_id'],
      metadata['contactId'],
      metadata['contact_id'],
      metadata['leadId'],
      metadata['lead_id'],
      data['contactId'],
      data['contact_id'],
      data['leadId'],
      data['lead_id'],
      notification.senderId,
      raw['sender_id'],
      metadata['senderId'],
      metadata['sender_id'],
    ];

    for (const candidate of candidates) {
      if (candidate !== null && candidate !== undefined && candidate !== '') {
        const id = Number(candidate);
        if (!Number.isNaN(id) && id > 0) {
          return id;
        }
      }
    }

    return null;
  }

  private _resolveInboxId(notification: INotificationDto): number | null {
    const raw = notification as unknown as Record<string, unknown>;
    const metadata = this._getMetadata(raw);
    const data =
      raw['data'] && typeof raw['data'] === 'object'
        ? (raw['data'] as Record<string, unknown>)
        : {};

    const candidates = [
      raw['inboxId'],
      raw['inbox_id'],
      raw['conversationId'],
      raw['conversation_id'],
      metadata['inboxId'],
      metadata['inbox_id'],
      metadata['conversationId'],
      metadata['conversation_id'],
      data['inboxId'],
      data['inbox_id'],
      data['conversationId'],
      data['conversation_id'],
    ];

    for (const candidate of candidates) {
      if (candidate !== null && candidate !== undefined && candidate !== '') {
        const id = Number(candidate);
        if (!Number.isNaN(id) && id > 0) {
          return id;
        }
      }
    }

    return null;
  }
}
